import { db } from '../firebase';
import firebase from 'firebase/app';

/**
 * Service to fetch and aggregate page view & screen engagement analytics from Firestore.
 * Supports date range filtering ('today', 'yesterday', '7days', '14days', '30days', 'thisMonth', 'custom', 'all')
 * and calculates daily page view count and daily trends.
 */

export const getPageAnalytics = async (options = {}) => {
  const {
    range = '7days',
    startDate: customStartDate = null,
    endDate: customEndDate = null,
  } = options;

  try {
    const now = new Date();
    let start = new Date();
    let end = new Date();

    if (range === 'today') {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    } else if (range === 'yesterday') {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999);
    } else if (range === '7days') {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    } else if (range === '14days') {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 13, 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    } else if (range === '30days') {
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29, 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    } else if (range === 'thisMonth') {
      start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    } else if (range === 'custom') {
      if (customStartDate) {
        const s = new Date(customStartDate);
        start = new Date(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0, 0);
      } else {
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0, 0);
      }
      if (customEndDate) {
        const e = new Date(customEndDate);
        end = new Date(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59, 999);
      } else {
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      }
    } else if (range === 'all') {
      // For all time, we build a 30-day daily trend for visualization, and fetch all-time totals
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29, 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    }

    // Build day map for all days within [start, end]
    const dailyMap = {};
    const cur = new Date(start);
    while (cur <= end) {
      const dateKey = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
      const label = cur.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const dayName = cur.toLocaleDateString('en-US', { weekday: 'short' });
      dailyMap[dateKey] = {
        dateKey,
        label,
        dayName,
        views: 0,
        totalSeconds: 0,
        uniqueUsers: new Set(),
        topPageViews: 0,
        topPageName: '',
        pages: {},
      };
      cur.setDate(cur.getDate() + 1);
    }

    const pageMap = {};
    let totalViews = 0;
    let totalSeconds = 0;
    const overallUniqueUsers = new Set();

    // Query Firestore page_views
    let snapshot = null;
    if (range !== 'all') {
      const startTimestamp = firebase.firestore.Timestamp.fromDate(start);
      const endTimestamp = firebase.firestore.Timestamp.fromDate(end);

      try {
        snapshot = await db
          .collection('page_views')
          .where('timestamp', '>=', startTimestamp)
          .where('timestamp', '<=', endTimestamp)
          .get();
      } catch (err1) {
        console.warn('Timestamp query error in page_views, trying date string range:', err1);
        try {
          const startStr = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
          const endStr = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
          snapshot = await db
            .collection('page_views')
            .where('date', '>=', startStr)
            .where('date', '<=', endStr)
            .get();
        } catch (err2) {
          console.warn('Date string query also failed, using fallback scan:', err2);
          snapshot = await db.collection('page_views').limit(1000).get();
        }
      }
    } else {
      // Range is 'all': scan recent page_views for trend, and page_analytics for totals
      try {
        snapshot = await db.collection('page_views').orderBy('timestamp', 'desc').limit(1000).get();
      } catch (err) {
        snapshot = await db.collection('page_views').limit(500).get();
      }
    }

    if (snapshot && !snapshot.empty) {
      snapshot.forEach((doc) => {
        const data = doc.data();
        let dKey = null;

        if (data.date && typeof data.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(data.date)) {
          dKey = data.date;
        } else if (data.timestamp) {
          const t = data.timestamp.toDate ? data.timestamp.toDate() : new Date(data.timestamp);
          if (!isNaN(t.getTime())) {
            dKey = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
          }
        }

        const pName = data.pageName || data.route || 'Unknown Screen';
        const route = data.route || '';
        const duration = Number(data.durationSeconds) || 0;
        const uid = data.userId || null;

        // If doc is inside our date range
        if (dKey && dailyMap[dKey]) {
          totalViews += 1;
          totalSeconds += duration;
          if (uid && uid !== 'guest') overallUniqueUsers.add(uid);

          dailyMap[dKey].views += 1;
          dailyMap[dKey].totalSeconds += duration;
          if (uid && uid !== 'guest') dailyMap[dKey].uniqueUsers.add(uid);
          dailyMap[dKey].pages[pName] = (dailyMap[dKey].pages[pName] || 0) + 1;
          if (dailyMap[dKey].pages[pName] > dailyMap[dKey].topPageViews) {
            dailyMap[dKey].topPageViews = dailyMap[dKey].pages[pName];
            dailyMap[dKey].topPageName = pName;
          }

          if (!pageMap[pName]) {
            pageMap[pName] = {
              id: pName,
              pageName: pName,
              route,
              totalViews: 0,
              totalSeconds: 0,
            };
          }
          pageMap[pName].totalViews += 1;
          pageMap[pName].totalSeconds += duration;
        } else if (range === 'all') {
          // Accumulate for all-time page counts
          totalViews += 1;
          totalSeconds += duration;
          if (uid && uid !== 'guest') overallUniqueUsers.add(uid);
          if (!pageMap[pName]) {
            pageMap[pName] = {
              id: pName,
              pageName: pName,
              route,
              totalViews: 0,
              totalSeconds: 0,
            };
          }
          pageMap[pName].totalViews += 1;
          pageMap[pName].totalSeconds += duration;
        }
      });
    }

    // If range is 'all' or no page_views logs are present, supplement from page_analytics
    if (range === 'all' || totalViews === 0) {
      try {
        const lifetimeSnap = await db.collection('page_analytics').get();
        if (!lifetimeSnap.empty && range === 'all') {
          let lifeViews = 0;
          let lifeSeconds = 0;
          lifetimeSnap.forEach((doc) => {
            const d = doc.data();
            const views = d.totalViews || 0;
            const seconds = d.totalSeconds || 0;
            lifeViews += views;
            lifeSeconds += seconds;
            pageMap[doc.id] = {
              id: doc.id,
              pageName: d.pageName || doc.id,
              route: d.route || '',
              totalViews: Math.max(pageMap[doc.id]?.totalViews || 0, views),
              totalSeconds: Math.max(pageMap[doc.id]?.totalSeconds || 0, seconds),
            };
          });
          totalViews = Math.max(totalViews, lifeViews);
          totalSeconds = Math.max(totalSeconds, lifeSeconds);
        }
      } catch (err) {
        console.warn('Error fetching page_analytics backup:', err);
      }
    }

    // Format dailyTrend list
    const dailyTrend = Object.values(dailyMap).map((d) => ({
      dateKey: d.dateKey,
      label: d.label,
      dayName: d.dayName,
      views: d.views,
      totalSeconds: d.totalSeconds,
      uniqueUsersCount: d.uniqueUsers.size,
      topPageName: d.topPageName || 'None',
      avgSeconds: d.views > 0 ? Math.round(d.totalSeconds / d.views) : 0,
    }));

    const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const todayViews = dailyMap[todayKey] ? dailyMap[todayKey].views : 0;

    const yDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const yKey = `${yDate.getFullYear()}-${String(yDate.getMonth() + 1).padStart(2, '0')}-${String(yDate.getDate()).padStart(2, '0')}`;
    const yesterdayViews = dailyMap[yKey] ? dailyMap[yKey].views : 0;

    const daysCount = Math.max(1, Object.keys(dailyMap).length);
    const dailyAverageViews = Math.round(totalViews / daysCount);

    let peakDay = { label: 'None', views: 0, dateKey: '' };
    dailyTrend.forEach((d) => {
      if (d.views >= peakDay.views && d.views > 0) {
        peakDay = { label: `${d.label} (${d.dayName})`, views: d.views, dateKey: d.dateKey };
      }
    });

    const pages = Object.values(pageMap)
      .map((p) => ({
        ...p,
        avgSeconds: p.totalViews > 0 ? Math.round(p.totalSeconds / p.totalViews) : 0,
        percentage: totalViews > 0 ? Number(((p.totalViews / totalViews) * 100).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.totalViews - a.totalViews);

    return {
      pages,
      totalViews,
      totalSeconds,
      avgSecondsOverall: totalViews > 0 ? Math.round(totalSeconds / totalViews) : 0,
      dailyTrend,
      dailyAverageViews,
      todayViews,
      yesterdayViews,
      uniqueUsersCount: overallUniqueUsers.size,
      peakDay,
      startDateStr: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`,
      endDateStr: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`,
      daysCount,
      range,
    };
  } catch (error) {
    console.error('Error fetching page analytics:', error);
    return {
      pages: [],
      totalViews: 0,
      totalSeconds: 0,
      avgSecondsOverall: 0,
      dailyTrend: [],
      dailyAverageViews: 0,
      todayViews: 0,
      yesterdayViews: 0,
      uniqueUsersCount: 0,
      peakDay: { label: 'None', views: 0 },
      daysCount: 1,
      range,
    };
  }
};

export const getRecentPageViews = async (limit = 10) => {
  try {
    const snapshot = await db
      .collection('page_views')
      .orderBy('timestamp', 'desc')
      .limit(limit)
      .get();

    const logs = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      logs.push({
        id: doc.id,
        pageName: data.pageName || 'Unknown',
        route: data.route || '',
        userId: data.userId || 'guest',
        durationSeconds: data.durationSeconds || 0,
        platform: data.platform || 'android',
        timestamp: data.timestamp ? data.timestamp.toDate() : new Date(),
      });
    });
    return logs;
  } catch (error) {
    console.error('Error fetching recent page views:', error);
    return [];
  }
};
